// Porcje produktu: produkt może mieć wagę jednej porcji (`portion_weight`, gramy)
// i jej nazwę („sztuka", „plaster"). API składników, zestawów i listy zakupów
// przyjmuje i zwraca wyłącznie gramy — porcje istnieją tylko we froncie, więc
// przeliczamy w obie strony tutaj.

import { intlLocale } from '@/composables/useLanguage.js';
import { t } from '@/i18n/index.js';

export const hasPortion = (product) => Number(product?.portion_weight) > 0;

export const portionName = (product) => product?.portion_name?.trim() || t('food.portions.defaultName');

// Domyślny tryb wpisywania ilości dla produktu.
export const defaultMode = (product) => (hasPortion(product) && product.unit === 'portion' ? 'portion' : 'g');

// Liczba z pola: „1,5" i „1.5" znaczą to samo.
export const parseAmount = (value) => {
    const n = Number(String(value ?? '').trim().replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
};

export const toGrams = (amount, mode, product) => (mode === 'portion' && hasPortion(product)
    ? Math.round(amount * Number(product.portion_weight))
    : Math.round(amount));

export const toPortions = (weight, product) => (hasPortion(product)
    ? Math.round((Number(weight) / Number(product.portion_weight)) * 100) / 100
    : null);

// „Ładna" liczba porcji to wielokrotność ćwiartki, z której po zaokrągleniu
// wychodzą dokładnie te gramy — 1,37 sztuki otwieramy w gramach.
export const nicePortions = (weight, product) => {
    if (!hasPortion(product) || !(Number(weight) > 0)) return null;
    const quarter = Math.round((Number(weight) / Number(product.portion_weight)) * 4) / 4;
    return quarter > 0 && toGrams(quarter, 'portion', product) === Math.round(Number(weight)) ? quarter : null;
};

export const formatNumber = (n) => Number(n).toLocaleString(intlLocale(), { maximumFractionDigits: 2 });

// „2 × sztuka (120 g)" dla produktu liczonego w porcjach, inaczej „120 g".
// Mnożnik zamiast odmiany: nazwa porcji jest wpisana w jednej formie.
export const formatAmount = (weight, product) => {
    if (weight == null || weight === '') return '';
    const w = Math.round(Number(weight));
    if (product?.unit !== 'portion' || !hasPortion(product)) return `${w} g`;
    return `${formatNumber(toPortions(w, product))} × ${portionName(product)} (${w} g)`;
};

// Wybór produktu z listy od razu dokłada go w wadze z pola obok. Gdy to pole
// zostało przy domyślnych gramach, a produkt liczy się w porcjach — bierzemy
// jedną porcję, bo „100 g jajka" nikt nie miał na myśli.
export const pickedWeight = (product, typed, fallback = 100) => {
    const w = Math.round(parseAmount(typed)) || fallback;
    return w === fallback && defaultMode(product) === 'portion' ? Number(product.portion_weight) : w;
};

// Kalorie jednej porcji (makra produktu są na 100 g).
export const portionCalories = (product) => (hasPortion(product) && product.calories != null
    ? Math.round(Number(product.calories) * Number(product.portion_weight) / 100)
    : null);
